import {
  AnalyticsVisualizationAggregationType as AggregationType,
  AnalyticsVisualizationErrorBarType as ErrorBarType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {ScatterPlotConfig, ScatterPlotData, ScatterPlotProps} from 'ui-library/charting/ScatterPlot';
import aggregateCartesianPoints from 'ui-library/charting/statistics/aggregation/aggregateCartesianPoints';
import {ERROR_BAR_TYPE_TO_LABEL} from 'ui-library/charting/statistics/aggregation/Constants';
import getAggregatedAxisName from 'ui-library/charting/statistics/aggregation/getAggregatedAxisName';
import {CartesianPoint, Dataset, isLabeledDataset} from 'ui-library/charting/Types';

import {convertScatterPlotDataToDataFrame} from '../ScatterPlotViewer';

type ScatterPlotParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationScatterPlotParametersNode'
>;

// TODO(BNCH-24546): Don't break out each dataset and call aggregateCartesianPoints directly
/**
 * Applies aggregation to each dataset within ScatterPlotData.
 */
export default function aggregateScatterPlotProps(
  {data, config, ...props}: ScatterPlotProps<CartesianPoint>,
  scatterPlotParameters: Pick<ScatterPlotParameters, 'aggregationType' | 'errorBarType' | 'outlierConfig'>
): ScatterPlotProps<CartesianPoint> {
  const aggregatedDataFrames = (data.datasets as Array<Dataset>).map((dataset, index) =>
    aggregateCartesianPoints(
      convertScatterPlotDataToDataFrame(data, index),
      scatterPlotParameters.aggregationType,
      scatterPlotParameters.errorBarType,
      scatterPlotParameters.outlierConfig
    )
  );

  const aggregatedDatasets = (data.datasets as Array<Dataset<CartesianPoint>>).map((dataset, index) =>
    isLabeledDataset(dataset)
      ? {...dataset, label: dataset.label, points: aggregatedDataFrames[index].points}
      : {...dataset, points: aggregatedDataFrames[index].points}
  );

  return {
    ...props,
    data: {
      axisTypes: aggregatedDataFrames[0].axisTypes,
      datasets: aggregatedDatasets,
    } as ScatterPlotData<CartesianPoint>,
    config: {
      ...config,
      ...getConfigOverride(config, scatterPlotParameters.aggregationType, scatterPlotParameters.errorBarType),
    },
  };
}

export function getConfigOverride(
  config: ScatterPlotConfig,
  aggregationType: AggregationType | null,
  errorBarType: ErrorBarType | null
): Partial<ScatterPlotConfig> {
  return aggregationType == null
    ? {}
    : {
        yAxis: {...config.yAxis, label: getAggregatedAxisName(aggregationType, config.yAxis.label)},
        ...(errorBarType == null
          ? {}
          : {errorBarConfig: {errorValueLabel: ERROR_BAR_TYPE_TO_LABEL[errorBarType]}}),
      };
}
